import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Calendar as CalendarIcon, ClipboardCheck, Phone, MapPin, Clock, FileText, ShoppingCart, TrendingUp, Truck, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWithinInterval, parseISO, subMonths, subDays, startOfDay, endOfDay, startOfYear, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '@/api/AuthContext';

const COLORS = ['#3b82f6', '#8b5cf6', '#f97316', '#10b981', '#f43f5e', '#06b6d4'];

export default function AgentReport() {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['report-activities', user?.email, dateRange],
    queryFn: async () => {
      const all = await base44.entities.ActivityReport.filter({ agent_email: user?.email });
      return all.filter(a => {
        const date = parseISO(a.report_date);
        return isWithinInterval(date, { start: dateRange.from, end: dateRange.to });
      });
    },
    enabled: !!user?.email,
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['report-tasks', user?.email, dateRange],
    queryFn: async () => {
      const all = await base44.entities.Task.filter({ assigned_agent: user?.email });
      return all.filter(t => {
        if (!t.completed_date) return false;
        const date = parseISO(t.completed_date);
        return isWithinInterval(date, { start: dateRange.from, end: dateRange.to });
      });
    },
    enabled: !!user?.email,
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['report-orders', user?.email, dateRange],
    queryFn: async () => {
      const all = await base44.entities.Order.filter({ agent_email: user?.email });
      return all.filter(o => {
        const date = parseISO(o.created_date);
        return isWithinInterval(date, { start: dateRange.from, end: dateRange.to });
      });
    },
    enabled: !!user?.email,
  });

  const stats = {
    totalTasks: tasks.length,
    visits: activities.filter(a => a.activity_type === 'Besuch').length,
    calls: activities.filter(a => a.activity_type === 'Anruf').length,
    offers: activities.filter(a => a.activity_type === 'Angebot').length,
    followups: activities.filter(a => a.activity_type === 'Nachfassen').length,
    deliveries: activities.filter(a => a.activity_type === 'Lieferung').length,
    complaints: activities.filter(a => a.activity_type === 'Reklamation').length,
    proposals: orders.filter(o => o.order_type === 'Angebot').length,
    ordersCount: orders.filter(o => o.order_type === 'Bestellung').length,
  };

  // Activity distribution for pie chart
  const activityDistribution = [
    { name: 'Besuche', value: stats.visits, color: '#3b82f6' },
    { name: 'Anrufe', value: stats.calls, color: '#8b5cf6' },
    { name: 'Nachfassen', value: stats.followups, color: '#06b6d4' },
    { name: 'Angebote', value: stats.offers, color: '#f97316' },
    { name: 'Lieferungen', value: stats.deliveries, color: '#10b981' },
    { name: 'Reklamationen', value: stats.complaints, color: '#f43f5e' },
  ].filter(a => a.value > 0);

  // Daily activity for bar chart
  const days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
  const dailyData = days.map(day => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const dayActivities = activities.filter(a => a.report_date === dayStr);
    return {
      date: format(day, 'dd.MM'),
      activities: dayActivities.length,
    };
  }).filter((_, i, arr) => arr.length <= 14 || i % Math.ceil(arr.length / 14) === 0);

  const statCards = [
    { label: 'Aufgaben erledigt', value: stats.totalTasks, icon: ClipboardCheck, color: 'bg-blue-500' },
    { label: 'Besuche', value: stats.visits, icon: MapPin, color: 'bg-purple-500' },
    { label: 'Anrufe', value: stats.calls, icon: Phone, color: 'bg-cyan-500' },
    { label: 'Nachfassen', value: stats.followups, icon: Clock, color: 'bg-amber-500' },
    { label: 'Lieferungen', value: stats.deliveries, icon: Truck, color: 'bg-teal-500' },
    { label: 'Reklamationen', value: stats.complaints, icon: AlertCircle, color: 'bg-rose-500' },
    { label: 'Angebote', value: stats.proposals, icon: FileText, color: 'bg-orange-500' },
    { label: 'Bestellungen', value: stats.ordersCount, icon: ShoppingCart, color: 'bg-emerald-500' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Link to={createPageUrl('AgentProfile')}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <PageHeader
              title="Bericht"
              subtitle={<>Leistungsübersicht</>}
            />
          </div>

          {/* Date Range Picker */}
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDateRange({
                  from: startOfMonth(new Date()),
                  to: endOfMonth(new Date()),
                })}
              >
                Diesen Monat
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const last = subMonths(new Date(), 1);
                  setDateRange({ from: startOfMonth(last), to: endOfMonth(last) });
                }}
              >
                Letzten Monat
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDateRange({
                  from: startOfDay(subDays(new Date(), 29)),
                  to: endOfDay(new Date()),
                })}
              >
                Letzte 30 Tage
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDateRange({
                  from: startOfYear(new Date()),
                  to: endOfDay(new Date()),
                })}
              >
                Dieses Jahr
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-1 justify-start">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Von: {format(dateRange.from, 'dd. MMM yyyy', { locale: de })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange.from}
                    onSelect={(date) => {
                      if (!date || !isValid(date)) return;
                      const next = startOfDay(date);
                      setDateRange((prev) => ({
                        from: next,
                        to: prev.to && prev.to >= next ? prev.to : endOfDay(next),
                      }));
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-1 justify-start">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Bis: {format(dateRange.to, 'dd. MMM yyyy', { locale: de })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateRange.to}
                    onSelect={(date) => {
                      if (!date || !isValid(date)) return;
                      const next = endOfDay(date);
                      setDateRange((prev) => ({
                        from: prev.from && prev.from <= next ? prev.from : startOfDay(next),
                        to: next,
                      }));
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {statCards.map((stat, i) => (
            <Card key={i} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Activity Chart */}
        {dailyData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Aktivitäten pro Tag
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                    <Bar dataKey="activities" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity Distribution */}
        {activityDistribution.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Aktivitätsverteilung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="w-36 h-36 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={activityDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={60}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {activityDistribution.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full space-y-2">
                  {activityDistribution.map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-slate-600">{item.name}</span>
                      </div>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
